/**
 * DOCUMENTATION:
 * Component: UiUxVisualHierarchy
 * Purpose: Educational page detailing how to structure information so users can naturally parse it.
 * * * Note for Junior Developers:
 * - If everything stands out, nothing stands out. Visual hierarchy is about prioritization.
 * - Whitespace (Negative Space) is an active design element, not just "empty" space.
 * - Use the Law of Proximity: Things that are related should be placed close together. Things that are unrelated should be placed further apart.
 */

import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
} from "@mui/material";

/* ✅ Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

/* ✅ Assets */
const bgImage = "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ✅ Styled Components - Clean Code Principles */

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
    [theme.breakpoints.down("sm")]: {
        paddingTop: theme.spacing(8),
    },
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeContainer = styled(Box)(({ theme }) => ({
    position: "relative",
    marginTop: theme.spacing(2),
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ResponsiveDataRow = styled(Box)(({ theme }) => ({
    display: "grid",
    gridTemplateColumns: "1fr 1.5fr 2fr",
    gap: "15px",
    padding: "12px",
    borderBottom: "1px solid rgba(255,255,255,0.1)",
    "&:last-child": { borderBottom: "none" },
    [theme.breakpoints.down("md")]: {
        gridTemplateColumns: "1fr",
        gap: "5px",
        padding: "15px",
    },
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    textTransform: "uppercase",
    "&:hover": {
        backgroundColor: "#e0b200",
        transform: "scale(1.05)",
    },
}));

/* ✅ Main Component */
export default function UiUxVisualHierarchy() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    /* UI State */
    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);

    /* Scroll Listener for FAB */
    useEffect(() => {
        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, []);

    const handleScrollTop = () => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    /* Copy to Clipboard Handler */
    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    /* ---------------- DATA DEFINITIONS ---------------- */

    // Core Visual Hierarchy Concepts
    const hierarchyConcepts = useMemo(() => [
        { concept: "Whitespace (Negative Space)", rule: "Let it Breathe", description: "Empty space around elements. It reduces cognitive load, separates distinct sections, and draws the eye to the actual content." },
        { concept: "Gestalt: Proximity", rule: "Group Related Items", description: "The human brain assumes that elements placed close together are related. Use margins to group elements logically (e.g., a label and its input)." },
        { concept: "Z-Pattern & F-Pattern", rule: "Guide the Eye", description: "Users scan screens predictably. The Z-Pattern is great for landing pages (Top left to top right, down to bottom left). The F-Pattern is for text-heavy pages." },
        { concept: "Scale and Contrast", rule: "Make the Important Big", description: "Use massive scale differences (not just subtle tweaks) to immediately communicate what the user should look at first." },
    ], []);

    // Coding examples demonstrating how Hierarchy translates to CSS
    const codingModules = useMemo(() => [
        {
            title: "1. The Power of Whitespace (Instead of Lines)",
            code: `/* ❌ BAD: Overusing borders to separate content creates visual clutter */\n.article-bad {\n  border-bottom: 1px solid #ccc;\n  margin-bottom: 10px;\n}\n\n/* ✅ GOOD: Using aggressive whitespace creates a clean, modern hierarchy */\n.article-good {\n  margin-bottom: 4rem; /* Massive space separates distinct topics */\n}\n.article-good__title {\n  margin-bottom: 0.5rem; /* Tight space binds the title to the paragraph */\n}`,
            desc: "Junior developers often try to separate content by drawing boxes or lines around everything. Senior developers remove the lines and simply increase the whitespace. It is cleaner, more modern, and easier to read."
        },
        {
            title: "2. Law of Proximity (Form Design)",
            code: `/* CSS Grid is perfect for grouping related elements (Gestalt Proximity) */\n.form-group {\n  display: flex;\n  flex-direction: column;\n  /* 1. Tight gap between the Label and the Input (They are related!) */\n  gap: 0.25rem;\n  \n  /* 2. Large margin below the group to separate it from the next input */\n  margin-bottom: 2rem;\n}`,
            desc: "If the space between Input A and Input B is the same as the space between Input A and Label A, the user's brain gets confused. By using a tight `gap` internally and a large `margin-bottom` externally, you create distinct, scannable groups."
        },
        {
            title: "3. Implementing the Z-Pattern Layout",
            code: `/* A classic Z-Pattern Landing Page Hero Section */\n.hero-section {\n  display: grid;\n  grid-template-columns: 1fr 1fr;\n  align-items: center;\n  gap: 4rem;\n  padding: 4rem;\n}\n\n.hero-content {\n  /* Left side: The user starts reading here */\n  display: flex;\n  flex-direction: column;\n  align-items: flex-start;\n  gap: 1.5rem;\n}\n\n.hero-image {\n  /* Right side: The user's eye lands here next */\n  width: 100%;\n  border-radius: 12px;\n}`,
            desc: "The Z-Pattern relies on the fact that Western users read left-to-right, top-to-bottom. We place the logo top-left, nav links top-right, a massive headline middle-left, and a supporting image middle-right. It's a proven psychological flow."
        }
    ], []);

    return (
        <PageWrapper>
            {/* ✅ Page Header */}
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography
                    variant={isMobile ? "h5" : "h3"}
                    sx={{
                        fontWeight: 800,
                        color: "goldenrod",
                        display: "inline-block",
                        px: { xs: 3, md: 6 },
                        py: 1.5,
                        borderRadius: "999px",
                        boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                    }}
                >
                    VISUAL HIERARCHY & LAYOUT
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* ✅ Theory Section */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Typography variant="h5" fontWeight={800} color="goldenrod" mb={2}>
                            Directing the User's Attention
                        </Typography>
                        <Typography mb={3} sx={{ opacity: 0.9, lineHeight: 1.6 }}>
                            When a user lands on your page, their brain subconsciously asks: <em>"What is the most important thing here? Where do I look next?"</em> If you don't answer those questions instantly through visual hierarchy, the user feels overwhelmed. By mastering scale, contrast, and psychological grouping principles (Gestalt), you can create layouts that feel completely effortless to navigate.
                        </Typography>

                        {/* ✅ Core Philosophies Table */}
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 4, mb: 2 }}>
                            <Avatar sx={{ bgcolor: "goldenrod", color: "#100720" }}>
                                <AiFillBulb size={24} />
                            </Avatar>
                            <Typography variant="h6" fontWeight={700}>Composition Principles</Typography>
                        </Box>

                        <Box sx={{ bgcolor: "rgba(255,255,255,0.03)", borderRadius: "10px", overflow: "hidden" }}>
                            {!isMobile && (
                                <ResponsiveDataRow sx={{ bgcolor: "rgba(218, 165, 32, 0.1)", color: "goldenrod", fontWeight: "bold" }}>
                                    <div>Concept</div>
                                    <div>Design Rule</div>
                                    <div>Description</div>
                                </ResponsiveDataRow>
                            )}
                            {hierarchyConcepts.map((item, i) => (
                                <ResponsiveDataRow key={i}>
                                    <Box sx={{ fontWeight: "bold", color: "goldenrod", fontFamily: "'Fira Code', monospace" }}>{item.concept}</Box>
                                    <Box sx={{ fontSize: "0.95rem", fontWeight: 500 }}>{item.rule}</Box>
                                    <Box sx={{ opacity: 0.8, fontSize: "0.85rem", lineHeight: 1.5 }}>{item.description}</Box>
                                </ResponsiveDataRow>
                            ))}
                        </Box>
                    </SectionCard>
                </Grid>

                {/* ✅ Coding Modules Section */}
                {codingModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            {/* Module Header & Action Buttons */}
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}>
                                        <AiOutlineRead size={24} />
                                    </Avatar>
                                    <Typography variant="h6" fontWeight={700}>
                                        {item.title}
                                    </Typography>
                                </Box>

                                <Stack direction="row" spacing={1}>
                                    <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => handleCopy(item.code, i)}
                                            sx={{
                                                color: "white",
                                                borderColor: "rgba(255,255,255,0.3)",
                                                "&:hover": { borderColor: "white" }
                                            }}
                                        >
                                            {copiedIndex === i ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                                        </Button>
                                    </Tooltip>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={<CodeIcon />}
                                            sx={{
                                                color: "goldenrod",
                                                borderColor: "goldenrod",
                                                "&:hover": { borderColor: "#e0b200", bgcolor: "rgba(218, 165, 32, 0.1)" }
                                            }}
                                        >
                                            Try it
                                        </Button>
                                    </Link>
                                </Stack>
                            </Box>

                            <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, lineHeight: 1.6 }}>{item.desc}</Typography>

                            {/* Code Block Presentation */}
                            <CodeContainer>
                                <CodeBlock>{item.code}</CodeBlock>
                            </CodeContainer>
                        </SectionCard>
                    </Grid>
                ))}

                {/* ✅ Footer Navigation */}
                <Grid item xs={12} textAlign="center" sx={{ mt: 4 }}>
                    <Link to="/uiux-dashboard" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">
                            BACK TO DASHBOARD
                        </ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Back to Top Button */}
            <Zoom in={showScroll}>
                <Fab
                    size="small"
                    onClick={handleScrollTop}
                    sx={{ position: "fixed", bottom: 25, right: 25, bgcolor: "goldenrod", "&:hover": { bgcolor: "#e0b200" } }}
                >
                    <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                </Fab>
            </Zoom>
        </PageWrapper>
    );
}
